"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Map = Map;
var _event = require("@visx/event");
var _d3Geo = require("d3-geo");
var _polished = require("polished");
var _react = _interopRequireWildcard(require("react"));
var _renderprops = require("react-spring/renderprops");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _topojsonClient = require("topojson-client");
var _dataTypesAndUtils = require("./dataTypesAndUtils");
var _Tooltip = _interopRequireDefault(require("./Tooltip"));
var _Content = _interopRequireDefault(require("./Tooltip/Content"));
var _Overlay = _interopRequireDefault(require("./Tooltip/Overlay"));
var _Header = _interopRequireDefault(require("./Tooltip/Header"));
var _geographyTopo = _interopRequireDefault(require("./geography.topo.json"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
/// <re

// NOTE: For some reason, there isn't a `topojson-specification` but only a `@types/topojson-specification`.
// eslint-disable-next-line import/no-unresolved
//
// Constants
// -------------------------------------------------------------------------------------------------
const TOOLTIP_TOP_OFFSET = 16;

//
// Styled components
// -------------------------------------------------------------------------------------------------

const Circle = _styledComponents.default.circle`
  fill: ${p => p.theme.colors.independence};
`;
const Path = _styledComponents.default.path`
  fill: ${p => p.theme.colors.antiFlashWhite};
  stroke: ${p => p.theme.colors.ashGrey};
  stroke-opacity: 0.25;
`;
const SVG = _styledComponents.default.svg`
  height: ${p => (0, _polished.rem)(p.height)};
  width: ${p => (0, _polished.rem)(p.width)};
`;

//
// Main component
// -------------------------------------------------------------------------------------------------

// x, y coordinates for map projection
// Keep a reference to the InputPoint for quick reference when calculating minimum distance
// x, y, radius, reference to initial data
/**
 * Draws a world map with optional dots placed on it.
 *
 * @alpha
 */
function Map({
  height = 560,
  maxRadius = 6,
  minRadius = 2,
  points = [],
  width = 800
}) {
  const [tooltipData, setTooltipData] = (0, _react.useState)();
  const [tooltipLeft, setTooltipLeft] = (0, _react.useState)(0);
  const [tooltipOpen, setTooltipOpen] = (0, _react.useState)(false);
  const [tooltipTop, setTooltipTop] = (0, _react.useState)(0);
  const svgRef = _react.default.useRef(null);
  const tooltipRef = _react.default.useRef(null);
  const projection = (0, _react.useMemo)(() => {
    return (0, _d3Geo.geoNaturalEarth1)().rotate([-10, 0, 0]).scale(175).translate([width * 0.5, height * 0.5]);
  }, [height, width]);
  const mapPaths = (0, _react.useMemo)(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topology = _geographyTopo.default;
    const geography = topology.objects[Object.keys(topology.objects)[0]];
    const {
      features
    } = (0, _topojsonClient.feature)(topology, geography);
    return features.reduce((acc, feat) => {
      // Don't bother drawing Antarctica
      if (feat.properties.CONTINENT === 'Antarctica') return acc;
      const path = (0, _d3Geo.geoPath)().projection(projection);
      const d = path(feat);
      if (!d) return acc;
      return [...acc, d];
    }, []);
  }, [projection]);

  // Translate InputPoints to Pointcoords
  const pointCoords = (0, _react.useMemo)(() => {
    const maxCount = points.reduce(function (prev, current) {
      return prev.count > current.count ? prev : current;
    }).count;
    const minCount = points.reduce((prev, current) => {
      return prev.count < current.count ? prev : current;
    }).count;
    return points.reduce((acc, coords) => {
      const {
        latitude,
        longitude,
        count
      } = coords;
      const point = projection([longitude, latitude]);
      const size = (0, _dataTypesAndUtils.scaleBetween)(count, minCount, maxCount, minRadius, maxRadius);
      if (!point) return acc;
      const [x, y] = point;
      return [...acc, {
        ...coords,
        size,
        x,
        y
      }];
    }, []);
  }, [maxRadius, minRadius, points, projection]);

  // Tooltip helpers
  const distanceToPoint = (x, y, point) => {
    const xDistance = point.x - x;
    const yDistance = point.y - y;
    const xSquared = xDistance * xDistance;
    const ySquared = yDistance * yDistance;
    return Math.sqrt(xSquared + ySquared);
  };
  const showTooltipAt = (positionX, positionY) => {
    const boundingRect = svgRef.current && svgRef.current.getBoundingClientRect();
    const {
      left,
      top
    } = boundingRect || {
      left: 0,
      top: 0
    };
    if (positionX < 0 || positionY < 0) {
      setTooltipOpen(false);
      return;
    }
    const tooltipWidth = tooltipRef.current ? tooltipRef.current.getBoundingClientRect().width : 0;
    const xOffset = 25;
    const yOffset = 50;
    const positionXWithOffset = positionX + xOffset;
    const pastRightSide = positionXWithOffset + tooltipWidth > width;
    const tooltipLeft = pastRightSide ? positionX - tooltipWidth - xOffset : positionXWithOffset + xOffset;
    const scrollHeight = window.pageYOffset;
    const extraTop = scrollHeight + top - TOOLTIP_TOP_OFFSET;
    const tooltipTop = positionY - yOffset + extraTop;
    setTooltipLeft(tooltipLeft + left);
    setTooltipTop(tooltipTop);
  };
  const mouseLeave = () => {
    setTooltipOpen(false);
  };
  const mouseMove = event => {
    const point = svgRef.current && (0, _event.localPoint)(svgRef.current, event);
    const {
      x,
      y
    } = point || {
      x: 0,
      y: 0
    };
    const distances = pointCoords.map(point => ({
      ...point,
      distance: distanceToPoint(x, y, point)
    }));
    const closestCoordinate = distances.reduce((prev, current) => {
      return prev.distance < current.distance ? prev : current;
    });
    if (closestCoordinate) {
      setTooltipData(closestCoordinate);
      setTooltipOpen(true);
      showTooltipAt(closestCoordinate.x, closestCoordinate.y);
    }
  };
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(SVG, {
    height: height,
    width: width,
    ref: svgRef
  }, mapPaths.map(path => /*#__PURE__*/_react.default.createElement(Path, {
    d: path,
    key: path
  })), pointCoords.map(({
    size,
    x,
    y
  }) => /*#__PURE__*/_react.default.createElement(Circle, {
    r: size,
    cx: x,
    cy: y,
    key: `${x} ${y}`
  })), /*#__PURE__*/_react.default.createElement(_Overlay.default, {
    onMouseLeave: mouseLeave,
    onMouseMove: mouseMove,
    tooltipData: tooltipData,
    tooltipOpen: tooltipOpen,
    xMax: width,
    yMax: height
  })), /*#__PURE__*/_react.default.createElement(_renderprops.Spring, {
    config: _renderprops.config.stiff,
    from: {
      left: tooltipLeft || 0,
      opacity: 0,
      top: tooltipTop || 0
    },
    to: {
      left: tooltipLeft || 0,
      opacity: tooltipOpen ? 0.9 : 0,
      top: tooltipTop || 0
    }
  }, ({
    left,
    opacity,
    top
  }) => /*#__PURE__*/_react.default.createElement(_Tooltip.default, {
    ref: tooltipRef,
    style: {
      left,
      top,
      opacity
    }
  }, tooltipData && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Header.default, {
    text: tooltipData.location
  }), /*#__PURE__*/_react.default.createElement(_Content.default, {
    title: `Malicious Email${tooltipData.count > 1 ? 's' : ''}`
  }, /* TODO: t('Statistic.number', { value: tooltipData[3].count })*/
  `${tooltipData.count.toLocaleString()}`)))));
}